// usePrecheckStream - fahrt die 5 Precheck-Phasen visuell durch und ruft
// pro Werk /api/transport/package-precheck (Concurrency 3, Timeout 120s).
// Am Ende /api/transport/package-references fuer die globalen Referenzen.
// Nutzt intern useCompareStream fuer Phase-Timing. Der Hook stammt aus der
// entfernten V1-Transport-App und liegt seitdem hier, weil nur noch V2 ihn braucht.

import { useCallback, useEffect, useRef, useState } from "react";
import { useCompareStream } from "./useCompareStream";
import { api } from "../../../utils/helpers";
import { useLang } from "../i18n";
import { plantNamingError } from "../../../utils/transportBatchErrors";
import { setDesignArtifacts } from "../utils/designArtifacts";
import { requestPlantNaming } from "../utils/plantNaming";

const CONCURRENCY = 3;

async function runInBatches(tasks, worker, size = CONCURRENCY) {
  const out = {};
  for (let i = 0; i < tasks.length; i += size) {
    const batch = tasks.slice(i, i + size);
    const res = await Promise.all(batch.map(async (t) => {
      try {
        const r = await worker(t);
        return { key: t.key, value: r };
      } catch (e) {
        if (e.name === "AbortError") throw e;
        return { key: t.key, value: { error: plantNamingError(e) || "precheck fehlgeschlagen" } };
      }
    }));
    for (const r of res) out[r.key] = r.value;
  }
  return out;
}

export function usePrecheckStream() {
  const { t } = useLang();
  const { phases, running, startCompare, reset: resetCompare } = useCompareStream();
  const controllerRef = useRef(null);
  useEffect(() => () => controllerRef.current?.abort(), []);
  const reset = useCallback(() => {
    controllerRef.current?.abort();
    resetCompare();
  }, [resetCompare]);
  const [precheck, setPrecheck] = useState({});
  const [refs, setRefs] = useState([]);
  const [refsWarning, setRefsWarning] = useState(null);
  const [error, setError] = useState(null);

  const run = useCallback(async ({ sourceInstanceId, sourcePackageId, targetInstances, werke, plantNaming, signal, concurrency = CONCURRENCY }) => {
    controllerRef.current?.abort();
    resetCompare();
    setDesignArtifacts(sourceInstanceId, sourcePackageId, []);
    const controller = new AbortController();
    controllerRef.current = controller;
    const abort = () => controller.abort();
    signal?.addEventListener("abort", abort);
    if (signal?.aborted) abort();
    const assertActive = () => {
      if (controller.signal.aborted) throw Object.assign(new Error("Comparison cancelled"), { name: "AbortError" });
    };
    const request = async (url, options = {}) => {
      assertActive();
      const result = await api(url, { retry: false, ...options, signal: controller.signal });
      assertActive();
      return result;
    };
    setPrecheck({});
    setRefs([]);
    setRefsWarning(null);
    setError(null);
    try {
      assertActive();
      const naming = requestPlantNaming(plantNaming);
      if (!sourceInstanceId || !sourcePackageId) return { precheck: {}, references: [] };
      const collected = await startCompare(api, {
        signal: controller.signal,
        // The scoped endpoints enforce access themselves. Reloading the complete
        // package catalog here adds no authorization or comparison information.
        fetchAuth: async () => true,
        fetchSource: async () => request(`/api/${sourceInstanceId}/transport/package-artifacts/${encodeURIComponent(sourcePackageId)}`),
        fetchTarget: async () => {
          const bucketList = werke && werke.length ? werke : [{ key: "std", kennung: "" }];
          const tasks = [];
          for (const inst of targetInstances || []) {
            for (const w of bucketList) {
              const key = `${inst.id}__${w.kennung || "std"}`;
              const body = {
                sourceInstanceId,
                targetInstanceId: inst.id,
                sourcePackageId,
                plantNaming: naming,
              };
              if (w.kennung) body.werkKennung = w.kennung;
              tasks.push({ key, body, werk: w.kennung ? w : null, instance: inst });
            }
          }
          const results = await runInBatches(tasks, async (t) => {
            const r = await request("/api/transport/package-precheck", {
              method: "POST",
              timeout: 120000,
              body: JSON.stringify(t.body),
            });
            return { ...r, werk: t.werk, instance: t.instance };
          }, concurrency);
          // Fehlerhafte Zielabfragen dürfen die letzte Liste nicht durch leere Zeilen ersetzen.
          const failures = Object.values(results).filter((r) => r.error || !Array.isArray(r.comparison));
          if (failures.length) throw new Error([...new Set(failures.map((r) => r.error || t("err.precheckResponse")))].join("; "));
          assertActive();
          setPrecheck(results);
          return results;
        },
        fetchRefs: async (_apiFn, _params, collected) => {
          const targets = collected?.target || {};
          const firstWithComparison = Object.values(targets).find((c) => c && Array.isArray(c.comparison));
          if (!firstWithComparison) { setRefs([]); return { references: [] }; }
          const srcIds = [...new Set(firstWithComparison.comparison.filter((a) => !a.draft).map((a) => a.originalId || a.Id))];
          if (srcIds.length === 0) { setRefs([]); return { references: [] }; }
          const firstTargetId = firstWithComparison.instance?.id || targetInstances?.[0]?.id;
          try {
            const r = await request("/api/transport/package-references", {
              method: "POST",
              timeout: 120000,
              body: JSON.stringify({ sourceInstanceId, targetInstanceId: firstTargetId, sourcePackageId, artifactIds: srcIds }),
            });
            const list = Array.isArray(r?.references) ? r.references : [];
            setRefs(list);
            if (Array.isArray(r?.failedArtifacts) && r.failedArtifacts.length > 0) {
              setRefsWarning(`Globale Referenzen unvollständig: ${r.failedArtifacts.length} von ${srcIds.length} Artefakten konnten nicht gescannt werden.`);
            }
            return r;
          } catch (e) {
            if (e.name === "AbortError") throw e;
            setRefs([]);
            setRefsWarning(e?.message || "Globale Referenzen konnten nicht geladen werden");
            return { references: [], incomplete: true };
          }
        },
        computeDiff: async () => true,
      });
      const design = Array.isArray(collected.source?.designArtifacts) ? collected.source.designArtifacts : [];
      setDesignArtifacts(sourceInstanceId, sourcePackageId, design);
      return { precheck: collected.target || {}, references: collected.refs?.references || [],
        referencesIncomplete: !!collected.refs?.incomplete || !!collected.refs?.failedArtifacts?.length };
    } catch (e) {
      if (!controller.signal.aborted) setError(plantNamingError(e) || "Vergleich fehlgeschlagen");
      throw e;
    } finally {
      signal?.removeEventListener("abort", abort);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startCompare, resetCompare, t]);

  return { phases, running, run, reset, precheck, refs, refsWarning, error };
}
